import { useState } from 'react'
import { Card, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'

// Importar recharts
import {
  BarChart,
  LineChart,
  PieChart,
  Bar,
  Line,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

const RechartsAvailable = true

const BudgetCharts = ({ budget }) => {
  const [periodoInicio, setPeriodoInicio] = useState('')
  const [periodoFin, setPeriodoFin] = useState('')

  // Obtener datos para gráficos
  const { data: datosGraficos, isLoading, error } = useQuery(
    ['budget-charts', budget.id, periodoInicio, periodoFin],
    async () => {
      const params = new URLSearchParams()
      if (periodoInicio) params.append('periodo_inicio', periodoInicio)
      if (periodoFin) params.append('periodo_fin', periodoFin)
      const response = await api.get(`/financial/budgets/${budget.id}/datos_graficos/?${params.toString()}`)
      return response.data
    },
    { enabled: !!budget.id }
  )

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d']

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando datos para gráficos...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar datos para gráficos: {error.message}
      </Alert>
    )
  }

  if (!datosGraficos) {
    return (
      <Alert variant="info">
        No hay datos disponibles para generar gráficos.
      </Alert>
    )
  }

  return (
    <div>
      {/* Filtros */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Período Inicio</Form.Label>
                <Form.Control
                  type="date"
                  value={periodoInicio}
                  onChange={(e) => setPeriodoInicio(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Período Fin</Form.Label>
                <Form.Control
                  type="date"
                  value={periodoFin}
                  onChange={(e) => setPeriodoFin(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!RechartsAvailable && (
        <Alert variant="warning" className="mb-3">
          <strong>Nota:</strong> Para ver gráficos interactivos, instala la librería recharts:
          <code className="ms-2">npm install recharts</code>
          <br />
          Por ahora se mostrarán tablas con los datos.
        </Alert>
      )}

      {/* Gráfico de Barras: Presupuestado vs Ejecutado */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Presupuestado vs Ejecutado por Línea</h5>
        </Card.Header>
        <Card.Body>
          {RechartsAvailable && datosGraficos.barras ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={datosGraficos.barras}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="linea" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value) => `${formatCurrency(value)}`} />
                <Legend />
                <Bar dataKey="presupuestado" fill="#8884d8" name="Presupuestado" />
                <Bar dataKey="ejecutado" fill="#82ca9d" name="Ejecutado" />
                {datosGraficos.barras[0]?.comprometido !== undefined && (
                  <Bar dataKey="comprometido" fill="#ffc658" name="Comprometido" />
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Línea</th>
                    <th className="text-end">Presupuestado</th>
                    <th className="text-end">Ejecutado</th>
                    <th className="text-end">Diferencia</th>
                  </tr>
                </thead>
                <tbody>
                  {datosGraficos.barras?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.linea}</td>
                      <td className="text-end">{formatCurrency(item.presupuestado)}</td>
                      <td className="text-end">{formatCurrency(item.ejecutado)}</td>
                      <td className="text-end">{formatCurrency((item.ejecutado - item.presupuestado))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Gráfico de Líneas: Evolución Mensual */}
      {datosGraficos.lineas && datosGraficos.lineas.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Evolución Mensual</h5>
          </Card.Header>
          <Card.Body>
            {RechartsAvailable ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={datosGraficos.lineas}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="periodo" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${formatCurrency(value)}`} />
                  <Legend />
                  <Line type="monotone" dataKey="presupuestado" stroke="#8884d8" name="Presupuestado" strokeWidth={2} />
                  <Line type="monotone" dataKey="ejecutado" stroke="#82ca9d" name="Ejecutado" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th className="text-end">Presupuestado</th>
                      <th className="text-end">Ejecutado</th>
                      <th className="text-end">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosGraficos.lineas.map((item, index) => (
                      <tr key={index}>
                        <td>{item.periodo}</td>
                        <td className="text-end">{formatCurrency(item.presupuestado)}</td>
                        <td className="text-end">{formatCurrency(item.ejecutado)}</td>
                        <td className="text-end">{formatCurrency((item.ejecutado - item.presupuestado))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Gráfico de Pastel: Distribución por Cuenta */}
      {datosGraficos.pastel && datosGraficos.pastel.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Distribución del Presupuesto por Cuenta</h5>
          </Card.Header>
          <Card.Body>
            {RechartsAvailable ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={datosGraficos.pastel}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, porcentaje }) => `${name}: ${porcentaje.toFixed(1)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {datosGraficos.pastel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${formatCurrency(value)}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Cuenta</th>
                      <th className="text-end">Monto</th>
                      <th className="text-end">Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosGraficos.pastel.map((item, index) => (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td className="text-end">{formatCurrency(item.value)}</td>
                        <td className="text-end">{item.porcentaje.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default BudgetCharts
